import React, { ChangeEvent, useEffect, useRef, useState } from 'react';

import {
  ArrowDownwardOutlined,
  DownloadOutlined,
  FileUploadOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNotifications } from 'hooks';

import { useSelector, useTranslation } from 'third-party';

import { useSettings } from 'modules/setting';

import { appSelectors, settingSelectors } from 'selectors';

interface ImportJsonDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (data: string) => void;
}

const ImportJsonDialog: React.FC<ImportJsonDialogProps> = ({
  open,
  onClose,
  onImport,
}) => {
  const ref = useRef<HTMLSelectElement>(null);
  const { t } = useTranslation();
  const { error } = useNotifications();
  const { loadTenantEmailTemplate } = useSettings();

  const currentTenant = useSelector(appSelectors.currentTenant);
  const selectedTemplate = useSelector(settingSelectors.selectedTemplate);
  const tenants = useSelector(appSelectors.tenants);

  const filteredTenants = tenants.filter(
    it => it.tenant.id != currentTenant?.id,
  );

  const [selectedTenantId, setSelectedTenantId] = useState<string>(
    filteredTenants[0]?.tenant.id || '',
  );

  useEffect(() => {
    if (open) {
      setSelectedTenantId(filteredTenants[0]?.tenant.id || '');
    }
  }, [open]);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      error(t('emailDesigner.error.invalidFile'));
      return;
    }

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const content = e.target?.result as string;
        JSON.parse(content);
        onImport(content);
        onClose();
      } catch {
        error(t('emailDesigner.error.invalidFile'));
      }
    };
    reader.readAsText(file);
  };

  const handleImportTenantChange = (event: SelectChangeEvent) => {
    setSelectedTenantId(event.target.value);
  };

  const handleTenantImport = async () => {
    const { payload } = await loadTenantEmailTemplate(
      selectedTenantId,
      selectedTemplate,
    );

    onImport(payload.value);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('emailDesigner.import.selectFileTitle')}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 2,
          }}
        >
          <Button
            variant="contained"
            component="label"
            startIcon={<DownloadOutlined />}
          >
            {t('emailDesigner.import.selectFile')}
            <input
              type="file"
              hidden
              accept=".json"
              onChange={handleFileUpload}
            />
          </Button>
          {tenants.length > 1 && (
            <Typography variant="h3">
              {t('emailDesigner.import.fromOrganization')}
            </Typography>
          )}
          <Select
            ref={ref}
            size="small"
            variant="standard"
            defaultValue={filteredTenants[0]?.tenant.id || ''}
            labelId="demo-select-small-label"
            id="demo-select-small"
            displayEmpty
            onChange={handleImportTenantChange}
            sx={{
              minWidth: '10rem',
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
            }}
          >
            {filteredTenants.map(it => (
              <MenuItem key={it.tenant.id} value={it.tenant.id}>
                {it.tenant.name}
              </MenuItem>
            ))}
          </Select>

          <Button
            variant="outlined"
            component="label"
            onClick={handleTenantImport}
            startIcon={<ArrowDownwardOutlined />}
          >
            {t('emailDesigner.import.buttonFromOrganization')}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.form.cancel')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export const ImportTemplate = () => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { error, success } = useNotifications();
  const { setEmailTemplate } = useSettings();
  const loadEmailTemplatePending = useSelector(
    settingSelectors.loadEmailTemplatePending,
  );
  const saveSettingPending = useSelector(settingSelectors.saveSettingPending);

  const disabledImport = saveSettingPending || loadEmailTemplatePending;

  const handleImport = (content: string) => {
    try {
      setEmailTemplate(JSON.parse(content));
      success(t('emailDesigner.import.senseful'));
    } catch {
      error(t('emailDesigner.error.invalidFile'));
    }
  };

  return (
    <>
      <Tooltip title={t('emailDesigner.actions.importTemplate')}>
        <IconButton disabled={disabledImport} onClick={() => setOpen(true)}>
          <FileUploadOutlined fontSize="small" />
        </IconButton>
      </Tooltip>

      <ImportJsonDialog
        open={open}
        onClose={() => setOpen(false)}
        onImport={handleImport}
      />
    </>
  );
};
